import "./FileInfo.scss";

import data from "./data";
import formatBytes from "./helpers/formatBytes";
import removeFile from "./helpers/removeFile";

/**
 * A map of field ID to a tuple containing 3 values:
 * * `string` - The input type for the field i.e. textarea, text, url or select
 * * `string` - The name of the i18n string in the this.parentForm.i18n object to use for the field.
 * * `string|undefined` - An optional string for the name of the data property to use for the field. For example a select field requires options.
 * @type {Map<string, [string, string, string?]>}
 */
const FieldMap = new Map([
    ["name", ["text", "fieldName", undefined, true]],
    ["email", ["email", "fieldEmail", undefined, true]],
    ["caption", ["textarea", "fieldCaption", undefined, false]],
    ["description", ["textarea", "fieldDescription", undefined, false]],
    ["alt", ["text", "fieldAlt", undefined, false]],
    ["custom_url", ["url", "fieldCustomUrl", undefined, false]],
    ["custom_target", ["select", "fieldCustomTarget", "customTargets", false]],
    ["tags", ["text", "fieldTags", undefined, false]]
]);

/**
 * A class to create and manage information for a single file upload.
 */
export default class FileInfo {
    /**
     * Create a new instance of the class.
     * @param {FileDialog} parent
     * @param {File} file
     */
    constructor( parent, file ) {
        // bind the context within internal listeners
        this.onRemoveClicked = this.onRemoveClicked.bind( this );

        /**
         * The parent `FileDialog` instance.
         * @type {FileDialog}
         * @readonly
         */
        this.parentFileDialog = parent;
        /**
         * The parent `Form` instance.
         * @type {Form}
         * @readonly
         */
        this.parentForm = parent.parentForm;
        /**
         * The `File` object to display information for.
         * @type {File}
         * @readonly
         */
        this.file = file;
        /**
         * The root/outer element for the file information.
         * @type {HTMLDivElement}
         * @readonly
         */
        this.element = this.createElement( file );
        /**
         * The thumbnail image for the file.
         * @type {HTMLImageElement}
         * @readonly
         */
        this.thumbnail = this.element.querySelector( ".fg-feu-file-thumbnail" );
        /**
         * The remove button for the file.
         * @type {HTMLButtonElement}
         * @readonly
         */
        this.buttonRemove = this.element.querySelector( ".fg-feu-file-remove" );
        this.buttonRemove?.addEventListener( "click", this.onRemoveClicked, { once: true } );
    }

    /**
     * Create the root/outer element for the file information.
     * @param {File} file
     * @return {HTMLDivElement}
     */
    createElement( file ) {
        const element = document.createElement( "section" );
        element.classList.add( "fg-feu-file" );
        element.innerHTML = `<figure class="fg-feu-file-image">
    <img class="fg-feu-file-thumbnail" alt="${ file.name }" src="" draggable="false"/>
    <figcaption class="fg-feu-file-info">
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileName }: <span>${ file.name }</span>
        </label>
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileType }: <span>${ file.type }</span>
        </label>
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileSize }: <span class="fg-feu-file-size">${ formatBytes( file.size ) }</span>
        </label>
        <label class="fg-feu-file-detail">
            ${ this.parentForm.i18n.fileDimensions }: <span class="fg-feu-file-dimensions">? x ?</span>
        </label>
        <button type="button" class="fg-feu-file-remove">${ this.parentForm.i18n.fileRemove }</button>
    </figcaption>
</figure>`;

        // update the dimensions one the thumbnail is loaded
        const dimensions = element.querySelector( ".fg-feu-file-dimensions" );
        const thumb = element.querySelector( ".fg-feu-file-thumbnail" );
        thumb.onload = () => {
            thumb.onload = null;
            dimensions.textContent = `${ thumb.naturalWidth } x ${ thumb.naturalHeight }`;
        }
        thumb.src = URL.createObjectURL( file );

        const fields = this.createFieldSetElement();
        if ( fields instanceof HTMLFieldSetElement ) element.append( fields );
        return element;
    }

    /**
     * If the parent `Form` has any fields, create them within a HTMLFieldSetElement and return it.
     * @return {HTMLFieldSetElement|null} `null` if the parent `Form` has no fields.
     */
    createFieldSetElement() {
        if (!this.parentForm.hasFields) return null;

        const fieldset = document.createElement("fieldset");
        fieldset.classList.add("fg-feu-file-fields");

        const isFirstFile = this.parentFileDialog.fileInfos.size === 0;
        const fileIndex = this.parentFileDialog.fileInfos.size;

        for (const field of this.parentForm.fields) {
            if (!FieldMap.has(field)) continue;
            const [type, i18nName, dataName, firstOnly] = FieldMap.get(field);

            let input;
            if (firstOnly && !isFirstFile) {
                input = document.createElement("input");
                input.type = "hidden";
                input.name = `${field}`;
                if (this.parentFileDialog._firstOnlyValues.has(field)) {
                    input.value = this.parentFileDialog._firstOnlyValues.get(field);
                }
            } else {
                switch (type) {
                    case "textarea":
                        input = document.createElement("textarea");
                        input.rows = 3;
                        break;
                    case "text":
                        input = document.createElement( "input" );
                        input.type = "text";
                        break;
                    case "email":
                        input = document.createElement("input");
                        input.type = type;
                        break;
                    case "url":
                        input = document.createElement("input");
                        input.type = "url";
                        break;
                    case "select":
                        input = document.createElement("select");
                        const options = Array.isArray(data[dataName]) ? data[dataName] : [];
                        for (let i = 0; i < options.length; i++) {
                            const option = document.createElement("option");
                            option.value = options[i].value;
                            option.textContent = options[i].label;
                            if (i === 0) option.selected = true;
                            input.append(option);
                        }
                        break;
                }

                input.classList.add("fg-feu-file-field-input");
                input.name = firstOnly ? field : `${field}[${fileIndex}]`;

                // Add required attribute if this field is required
                if (this.parentForm.requiredFields.includes(field)) {
                    input.required = true;
                    input.setAttribute('aria-required', 'true');
                }

                if (firstOnly && isFirstFile) {
                    input.addEventListener("input", (e) => {
                        this.parentFileDialog.syncFirstOnlyFields(field, e.target.value);
                    });
                }
            }

            if (!input) continue;

            if (!firstOnly || isFirstFile) {
                const label = document.createElement("label");
                label.classList.add("fg-feu-file-field");

                const span = document.createElement("span");
                span.classList.add("fg-feu-file-field-name");
                span.textContent = this.parentForm.i18n[i18nName];
                
                // Add required indicator
                if (this.parentForm.requiredFields.includes(field)) {
                    const required = document.createElement("span");
                    required.classList.add("fg-feu-required");
                    required.textContent = " *";
                    required.setAttribute('aria-label', 'required');
                    span.append(required);
                }

                label.append(span, input);
                fieldset.append(label);
            } else {
                fieldset.append(input);
            }
        }

        return fieldset;
    }

    /**
     * Clean up any resources and events listeners before removing the element.
     */
    remove() {
        URL.revokeObjectURL( this.thumbnail.src );
        this.buttonRemove?.removeEventListener( "click", this.onRemoveClicked );
        this.element.remove();
        this.parentFileDialog.fileInfos.delete( this.element );
    }

    /**
     * Handles the click event for the remove button.
     * @param {MouseEvent} event
     * @this {FileInfo}
     */
    onRemoveClicked( event ) {
        event.preventDefault();
        const elements = Array.from( this.parentFileDialog.body.querySelectorAll( ".fg-feu-file" ) );
        const index = elements.indexOf( this.element );
        if ( index !== -1 && removeFile( this.parentForm.fileInput, index ) ) {
            this.remove();
            this.parentFileDialog.checkFilesChanged();
        }
    }
};